/**
 * CALIFORNIA OVERTIME ON LOGGED HOURS.
 *
 * Anthony, 2026-10-05: "add meal breaks and overtime to the clock", the
 * same day he moved Nina, Jeremy and Ashley to W-2.
 *
 * The rule, per workweek and per person (Labor Code 510):
 *
 *   - Over 8 hours in a day is time and a half, over 12 is double.
 *   - Over 40 regular hours in the week is time and a half. Hours already
 *     paid as daily overtime do not count toward the 40 again.
 *   - On the 7th day worked in a row inside one workweek, the first 8
 *     hours are time and a half and the rest double.
 *
 * The workweek starts Monday, the same week the Pay screen pages through.
 *
 * FROM A DATE, NOT BACKWARDS. Hours before OT_FROM are paid exactly as they
 * always were. Older shifts did run past 8 hours on some days, and whether
 * anything is owed on them is a decision for Anthony and an accountant, not
 * something a deploy should quietly change in everybody's balance.
 *
 * Which row of a day becomes overtime only matters when two rows of the
 * same day carry different rates. They are taken in the order given, and
 * the later hours of the day are the overtime ones.
 */

/** The first day overtime is computed. A Monday, so no week is split. */
export const OT_FROM = "2026-10-05";

export type ShiftIn = { id: string; technician_id: string; worked_on: string; hours: number };
export type Split = { reg: number; ot: number; dt: number };

const DAY = 86400000;
const dayNum = (iso: string): number => Math.floor(Date.parse(`${iso}T00:00:00Z`) / DAY);
/** Monday-based week number. 1970-01-01 was a Thursday, so shift by 3. */
const weekOf = (iso: string): number => Math.floor((dayNum(iso) + 3) / 7);

export function splitOvertime(rows: ShiftIn[]): Map<string, Split> {
  const out = new Map<string, Split>();
  const groups = new Map<string, ShiftIn[]>();

  for (const r of rows) {
    const h = Number(r.hours) || 0;
    if (r.worked_on < OT_FROM) { out.set(r.id, { reg: h, ot: 0, dt: 0 }); continue; }
    const k = `${r.technician_id}|${weekOf(r.worked_on)}`;
    const g = groups.get(k);
    if (g) g.push(r); else groups.set(k, [r]);
  }

  for (const week of groups.values()) {
    const days = [...new Set(week.filter((r) => Number(r.hours) > 0).map((r) => r.worked_on))].sort();
    // Seven days worked in one Monday-Sunday week can only mean every day,
    // and the 7th is the Sunday.
    const seventh = days.length === 7 ? days[6] : null;
    const ordered = [...week].sort((a, b) => (a.worked_on < b.worked_on ? -1 : a.worked_on > b.worked_on ? 1 : 0));

    let weekReg = 0;
    const dayCum = new Map<string, number>();

    for (const r of ordered) {
      const s: Split = { reg: 0, ot: 0, dt: 0 };
      let left = Number(r.hours) || 0;
      let cum = dayCum.get(r.worked_on) ?? 0;
      const isSeventh = r.worked_on === seventh;

      while (left > 1e-9) {
        let tier: keyof Split;
        let cap: number;
        if (isSeventh) { tier = cum < 8 ? "ot" : "dt"; cap = cum < 8 ? 8 - cum : Infinity; }
        else if (cum < 8) { tier = "reg"; cap = 8 - cum; }
        else if (cum < 12) { tier = "ot"; cap = 12 - cum; }
        else { tier = "dt"; cap = Infinity; }

        const take = Math.min(left, cap);
        if (tier === "reg") {
          const room = Math.max(0, 40 - weekReg);
          const r2 = Math.min(take, room);
          s.reg += r2; weekReg += r2; s.ot += take - r2;
        } else {
          s[tier] += take;
        }
        cum += take; left -= take;
      }
      dayCum.set(r.worked_on, cum);
      out.set(r.id, { reg: round2(s.reg), ot: round2(s.ot), dt: round2(s.dt) });
    }
  }
  return out;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** What a split pays at a rate. Regular, plus half again, plus double. */
export const splitPay = (s: Split, rate: number): number =>
  rate * (s.reg + 1.5 * s.ot + 2 * s.dt);

/** Total of several splits, for a screen that shows one line per person. */
export function addSplits(splits: Iterable<Split>): Split {
  const t: Split = { reg: 0, ot: 0, dt: 0 };
  for (const s of splits) { t.reg += s.reg; t.ot += s.ot; t.dt += s.dt; }
  return { reg: round2(t.reg), ot: round2(t.ot), dt: round2(t.dt) };
}
